// src/hooks/use-waypoint-sessions.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { waypointSessionKeys } from "@/lib/query";

// ── Client-side types (mirror the API response shape) ────────────────────────

export type WaypointPersonaDisplay = { name: string; title: string; behavior: string };

export type WaypointScenarioDisplay = {
  id: string;
  slug: string;
  name: string;
  briefVariants: Array<{
    company: string;
    industry: string;
    situation: string;
    tension: string;
    objective: string;
  }>;
  winConditions: string[];
  roomRoles: Array<{ role: "primary" | "supporting" | "observer"; personaSlug: string }>;
  personaMap: Record<string, WaypointPersonaDisplay>;
};

export type WaypointDebrief = {
  id: string;
  competencyScores: Record<string, { rating: number; anchorTurnSequence: number }>;
  annotatedTurns: Array<{
    sequence: number;
    competency: string;
    rating: number;
    rewrite: string;
  }>;
  prosodyReport: { fillerCounts: Record<string, number>; wpm: number };
  oneRecommendation: {
    competencySlug: string;
    fromRating: number;
    toRating: number;
    actions: { "10": string; "20": string; "70": string };
  };
  seedReveal: Record<string, unknown>;
  createdAt: string;
};

export type WaypointSessionSeed = {
  company: string;
  industry: string;
  briefVariantIndex: number;
  objectionsDrawn: string[];
  personaMoods: Record<string, string>;
  roomChemistry: Record<string, string>;
};

export type WaypointSessionDetail = {
  id: string;
  userId: string;
  difficulty: "foundation" | "standard" | "stretch";
  seed: WaypointSessionSeed;
  status: "briefing" | "active" | "ended";
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  scenario: WaypointScenarioDisplay | null;
  debrief: WaypointDebrief | null;
};

export type WaypointSessionSummary = Omit<WaypointSessionDetail, "scenario" | "debrief"> & {
  scenarioName: string | null;
};

export interface SessionListResponse {
  data: WaypointSessionSummary[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useWaypointSessions() {
  return useQuery({
    queryKey: waypointSessionKeys.list(),
    queryFn: async (): Promise<SessionListResponse> => {
      const res = await fetch("/api/waypoint/sessions?limit=5&sort=createdAt&sortDir=desc");
      if (!res.ok) throw new Error("Failed to fetch sessions");
      return res.json();
    },
  });
}

export function useWaypointSessionHistory(params: {
  cursor?: string | null;
  direction?: "next" | "prev";
}) {
  return useQuery({
    queryKey: waypointSessionKeys.history(params),
    queryFn: async (): Promise<SessionListResponse> => {
      const qs = new URLSearchParams({ limit: "20", sort: "createdAt", sortDir: "desc" });
      if (params.cursor) qs.set("cursor", params.cursor);
      if (params.direction) qs.set("direction", params.direction);
      const res = await fetch(`/api/waypoint/sessions?${qs}`);
      if (!res.ok) throw new Error("Failed to fetch sessions");
      return res.json();
    },
  });
}

export function useWaypointSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: waypointSessionKeys.detail(sessionId ?? ""),
    enabled: Boolean(sessionId),
    queryFn: async (): Promise<WaypointSessionDetail> => {
      const res = await fetch(`/api/waypoint/sessions/${sessionId}`);
      if (!res.ok) throw new Error("Failed to fetch session");
      return res.json();
    },
    // Poll every 3s until the debrief arrives — covers both "active" (WS just
    // closed, orch.end() still running) and "ended" (debrief generating).
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      if (!data.debrief) return 3000;
      return false;
    },
  });
}

export type CompetencyDimension = { slug: string; name: string; score: number };

export function useCompetencyProfile() {
  return useQuery({
    queryKey: waypointSessionKeys.profile(),
    queryFn: async (): Promise<{ dimensions: CompetencyDimension[]; sessionCount: number }> => {
      const res = await fetch("/api/waypoint/competency-profile");
      if (!res.ok) throw new Error("Failed to fetch competency profile");
      return res.json();
    },
  });
}

export function useCreateWaypointSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      scenarioSlug: string;
      difficulty?: "foundation" | "standard" | "stretch";
    }): Promise<WaypointSessionDetail> => {
      const res = await fetch("/api/waypoint/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...input, difficulty: input.difficulty ?? "standard" }),
      });
      if (!res.ok) throw new Error("Failed to create session");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: waypointSessionKeys.all });
    },
    onError: () => {
      toast.error("Failed to create session");
    },
  });
}
